// src/pages/ChangePasswordPage.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';

export default function ChangePasswordPage() {
  const [userId, setUserId] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser && (storedUser.USER_ID || storedUser.username)) {
      setUserId(storedUser.USER_ID || storedUser.username);
    } else {
      setErrorMsg('User information not found. Please log in again.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setErrorMsg('Please fill in all fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setErrorMsg('Password must be at least 8 characters long.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.put(`${API_URL}/api/oracle/users/${userId}/password`, {
        currentPassword,
        newPassword
      });

      setSuccessMsg(response.data.message || 'Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to change password.';
      setErrorMsg(message);
      console.error('Password change error:', err);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      padding: '20px',
      boxSizing: 'border-box',
    },
    form: {
      background: '#ffffff',
      padding: '40px',
      borderRadius: '12px',
      boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '420px',
      textAlign: 'center',
    },
    header: {
      marginBottom: '15px',
      color: '#333',
      fontSize: '24px',
      fontWeight: '600',
    },
    subHeader: {
      marginBottom: '25px',
      color: '#6c757d',
      fontSize: '16px',
    },
    userIdDisplay: {
      background: '#e9ecef',
      color: '#495057',
      padding: '8px 12px',
      borderRadius: '6px',
      marginBottom: '25px',
      fontSize: '14px',
      fontWeight: '500',
      display: 'inline-block',
    },
    inputGroup: {
      marginBottom: '20px',
      textAlign: 'left',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      color: '#555',
      fontSize: '14px',
      fontWeight: '500',
    },
    input: {
      width: '100%',
      padding: '12px 15px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '16px',
      boxSizing: 'border-box',
    },
    message: {
      padding: '10px',
      marginBottom: '15px',
      borderRadius: '6px',
      fontSize: '14px',
    },
    error: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      border: '1px solid #f5c6cb',
    },
    success: {
      backgroundColor: '#d4edda',
      color: '#155724',
      border: '1px solid #c3e6cb',
    },
    button: {
      width: '100%',
      padding: '12px',
      background: 'linear-gradient(45deg, #28a745, #218838)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
      marginTop: '10px',
    },
    buttonDisabled: {
      background: '#cccccc',
      cursor: 'not-allowed',
    }
  };

  return (
    <div style={styles.container}>
      <form onSubmit={handleSubmit} style={styles.form}>
        <h2 style={styles.header}>Change Password</h2>
        <p style={styles.subHeader}>Change your current account password.</p>

        <div style={styles.userIdDisplay}>
          Account ID: <strong>{userId || 'Not available'}</strong>
        </div>

        {errorMsg && <div style={{ ...styles.message, ...styles.error }}>{errorMsg}</div>}
        {successMsg && <div style={{ ...styles.message, ...styles.success }}>{successMsg}</div>}

        <div style={styles.inputGroup}>
          <label htmlFor="currentPassword" style={styles.label}>Current Password</label>
          <input
            id="currentPassword"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
            style={styles.input}
          />
        </div>

        <div style={styles.inputGroup}>
          <label htmlFor="newPassword" style={styles.label}>New Password</label>
          <input
            id="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            style={styles.input}
          />
        </div>

        <div style={styles.inputGroup}>
          <label htmlFor="confirmPassword" style={styles.label}>Confirm New Password</label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            style={styles.input}
          />
        </div>

        <button type="submit" disabled={loading} style={loading ? { ...styles.button, ...styles.buttonDisabled } : styles.button}>
          {loading ? 'Changing...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
}
